import { all, takeLatest, put, call } from 'redux-saga/effects';
import Api from 'src/helpers/api';
import { sendRequest } from 'src/helpers/saga';
import { logger } from 'src/helpers/logger';
import actions from './actions';
import actionTypes from './actionTypes';
import type { IRateEngineShort } from './types';

const api = new Api('pricing/engines/list');
// The server's ceiling; a larger value is silently clamped, so paging is the only way past it.
const PAGE_SIZE = 100;
// Stops the loop if the response keeps claiming a next page; no organisation reaches this many.
const MAX_PAGES = 50;

// The list must be complete: the wizard's pickers and rename validation are built from it, so a
// missing engine is one the administrator cannot reach or is wrongly told the name is free.
export function* fetchListShort(): Generator<any, void, any> {
  try {
    const records: IRateEngineShort[] = [];
    let page: number | null = 1;

    while (page && page <= MAX_PAGES) {
      // Annotated because the requested page comes from the previous response, so the yield's type is circular.
      // No failure action: `sendRequest` would dispatch a second FETCH_ERROR on top of this saga's own.
      const result: any = yield call(sendRequest, api.load, { search: { page, page_size: PAGE_SIZE } });
      const data: any = result?.data?.success?.data;

      if (result?.status !== 200 || !Array.isArray(data?.records)) {
        // Partial results are dropped on purpose: the reducer keeps the previous complete list.
        logger.error(`[rateEngine] engines list failed at page ${page}; discarding ${records.length} records`);
        yield put(actions.fetchError());

        return;
      }

      records.push(...data.records);

      const nextPage: number | null = data.pagination?.next_page ?? null;

      // A next_page that does not advance is a broken response, not the end of the list: following it would
      // re-read the same page to the ceiling, and stopping would store a partial list as complete.
      if (nextPage !== null && nextPage <= page) {
        logger.error(`[rateEngine] engines list next_page ${nextPage} did not advance past page ${page}`);
        yield put(actions.fetchError());

        return;
      }

      page = nextPage;
    }

    // Hitting the cap is a failure, not a result: a truncated list must not be stored as complete.
    if (page) {
      logger.error(`[rateEngine] engines list hit the ${MAX_PAGES}-page ceiling; ${records.length} records truncated`);
      yield put(actions.fetchError());

      return;
    }

    yield put(actions.fetchSuccess(records));
  } catch (error) {
    logger.error(error);
    yield put(actions.fetchError());
  }
}

export default function* rootSaga() {
  // takeLatest: a sweep is several round trips, so overlapping sweeps would race and the slower win.
  yield all([takeLatest(actionTypes.FETCH_REQUEST, fetchListShort)]);
}
